import { useState, useRef, useCallback } from 'react';

/**
 * Shared hook for lightbox zoom, pan, and drag behavior.
 * Used by Home.jsx (spotlight), CollectionPage.jsx (gallery lightbox).
 */
export function useLightbox() {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const panStartRef = useRef({ x: 0, y: 0 });
  const wasDraggingRef = useRef(false);

  const resetZoom = useCallback(() => {
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  }, []);

  const toggleZoom = useCallback((e) => {
    e.stopPropagation();
    setZoomLevel(prev => (prev === 1 ? 2.5 : 1));
    setPanPosition({ x: 0, y: 0 });
  }, []);

  const getCursor = () => {
    if (zoomLevel <= 1) return 'zoom-in';
    return isDragging ? 'grabbing' : 'zoom-out';
  };

  const getTransform = () =>
    `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`;

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    wasDraggingRef.current = false;
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    panStartRef.current = { ...panPosition };
  };

  const handleMouseMove = (e) => {
    if (!isDragging || zoomLevel <= 1) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
      wasDraggingRef.current = true;
    }
    setPanPosition({ x: panStartRef.current.x + dx, y: panStartRef.current.y + dy });
  };

  const handleMouseUp = (e) => {
    if (isDragging) {
      e.stopPropagation();
      if (!wasDraggingRef.current) toggleZoom(e);
    }
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    if (zoomLevel > 1 && e.touches.length === 1) {
      setIsDragging(true);
      wasDraggingRef.current = false;
      dragStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      panStartRef.current = { ...panPosition };
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging || zoomLevel <= 1 || e.touches.length !== 1) return;
    const dx = e.touches[0].clientX - dragStartRef.current.x;
    const dy = e.touches[0].clientY - dragStartRef.current.y;
    if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
      wasDraggingRef.current = true;
    }
    setPanPosition({ x: panStartRef.current.x + dx, y: panStartRef.current.y + dy });
  };

  const handleTouchEnd = () => setIsDragging(false);

  /** Returns true if overlay click should be suppressed (drag just ended). */
  const shouldSuppressClick = () => {
    if (wasDraggingRef.current) {
      wasDraggingRef.current = false;
      return true;
    }
    return false;
  };

  return {
    zoomLevel,
    panPosition,
    isDragging,
    resetZoom,
    toggleZoom,
    getCursor,
    getTransform,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    shouldSuppressClick,
  };
}
